import { Line, Mesh, Object3D, Points } from 'three';

import { isDefined } from '../../utils/variables';
import { StyledLines } from './styled-lines/styled-lines';

/**
 * The layer of a node of a drawing: ConvertPdfToGlb writes a node per PDF layer with
 * extras.pdf = { layer, visible }, and ConvertDwgToGlb a node per DWG layer with extras.dwg.
 */
export type DrawingLayer = { layer: string; visible: boolean };

/**
 * The IFC spatial structure, e.g. a storey of a building, which the elements (walls, beams etc.)
 * are in.
 */
const ifcSpatialClasses = new Set([
  'IfcSite',
  'IfcBuilding',
  'IfcBuildingStorey',
  'IfcFacility',
  'IfcFacilityPart',
  'IfcFacilityPartCommon',
  'IfcBridge',
  'IfcBridgePart',
  'IfcRoad',
  'IfcRoadPart',
  'IfcRailway',
  'IfcRailwayPart',
  'IfcMarineFacility',
  'IfcMarinePart',
  'IfcExternalSpatialElement',
  'IfcSpatialZone',
]);

/**
 * Gets the layer of the node, if it is a layer of a drawing, see DrawingLayer.
 */
export function getDrawingLayer(object: Object3D): DrawingLayer | undefined {
  const userData = object.userData as {
    pdf?: Partial<DrawingLayer>;
    dwg?: Partial<DrawingLayer>;
  };
  const extras = userData.pdf ?? userData.dwg;

  return typeof extras?.layer === 'string'
    ? { layer: extras.layer, visible: extras.visible !== false }
    : undefined;
}

/**
 * Hides the layers of the drawings that are hidden in the file, e.g. a PDF layer that is off.
 */
export function hideHiddenLayers(scene: Object3D) {
  scene.traverse((node) => {
    if (getDrawingLayer(node)?.visible === false) {
      node.visible = false;
    }
  });
}

/**
 * A node that can be hidden and shown, with the nodes below it that can.
 */
export type LayerTreeNode = {
  object: Object3D;
  name: string;
  /** The page number of a page of a PDF, which is named by it. */
  page?: number;
  /** The class of a node of an IFC model, e.g. IfcBuildingStorey, which an unnamed one is named by. */
  ifcClass?: string;
  children: LayerTreeNode[];
};

/**
 * A value of an IFC attribute, property or quantity, see FORMAT.md of ConvertIfcToGlb in
 * skymap-oda: a nested object is a complex property, or the bounds of a bounded value, and null is
 * the UNKNOWN of a LOGICAL.
 */
export type IfcValue = string | number | boolean | null | IfcValue[] | { [name: string]: IfcValue };

/** Named property or quantity sets, e.g. Pset_WallCommon. */
export type IfcSets = Record<string, Record<string, IfcValue>>;

/**
 * The IFC data of an object, which ConvertIfcToGlb writes in the extras of its node, or of a type
 * in asset.extras.ifc.types, which holds what all objects of the type share.
 */
export type IfcData = {
  class?: string;
  attributes?: Record<string, IfcValue>;
  /** The index of the object's type in asset.extras.ifc.types. */
  type?: number;
  propertySets?: IfcSets;
  quantitySets?: IfcSets;
  classifications?: Record<string, IfcValue>[];
  materials?: string[];
};

/**
 * The IFC data of an object merged with that of its type, as in FORMAT.md, with the class and
 * attributes of the type.
 */
export type IfcObject = Omit<IfcData, 'type'> & {
  type?: Pick<IfcData, 'class' | 'attributes'>;
};

const getIfcData = (object: Object3D) => object.userData.ifc as IfcData | undefined;

/**
 * Gets the IFC data of the node of an IFC object, merged with what its type holds, see IfcData.
 *
 * @param types asset.extras.ifc.types of the glb file.
 */
export function getIfcObject(object: Object3D, types: IfcData[] = []): IfcObject | undefined {
  const ifc = getIfcData(object);
  const type = ifc?.type !== undefined ? types[ifc.type] : undefined;

  if (!ifc || !type) {
    return ifc && { ...ifc, type: undefined };
  }

  const merged: IfcObject = {
    ...type,
    ...ifc,
    type: { class: type.class, attributes: type.attributes },
  };

  for (const kind of ['propertySets', 'quantitySets'] as const) {
    const sets: IfcSets = {};
    for (const [name, set] of Object.entries(type[kind] ?? {})) {
      sets[name] = { ...set };
    }
    for (const [name, set] of Object.entries(ifc[kind] ?? {})) {
      sets[name] = { ...sets[name], ...set };
    }

    merged[kind] = Object.keys(sets).length > 0 ? sets : undefined;
  }

  return merged;
}

/**
 * Gets the node of the IFC object that the object is part of, e.g. the node of a wall from one of
 * its meshes, which are below it when the wall has several materials. Not above the root.
 */
export function getIfcNode(object: Object3D, root?: Object3D): Object3D | undefined {
  for (let x: Object3D | null = object; x && x !== root; x = x.parent) {
    if (isDefined(getIfcData(x))) {
      return x;
    }
  }

  return undefined;
}

/**
 * Whether the node is of the IFC spatial structure, e.g. a storey, see ifcSpatialClasses.
 */
export const isIfcSpatial = (object: Object3D) =>
  ifcSpatialClasses.has(getIfcData(object)?.class ?? '');

/**
 * A layer of a drawing, or an object of an IFC model: its project, the spatial structure, and the
 * elements in it, down to the parts of an assembly.
 */
const isLayer = (object: Object3D) =>
  isDefined(getDrawingLayer(object)) || isDefined(getIfcData(object));

/**
 * Whether the node is from a converter (a PDF page, the model of a DWG, the IFC project etc.),
 * so it is shown in the tree when there are layers below it.
 */
const isConverterNode = (object: Object3D) =>
  isDefined(object.userData.pdf) ||
  isDefined(object.userData.dwg) ||
  isDefined(object.userData.ifc);

/**
 * Whether the object is a layer or below one, up to the root (not included).
 */
const isInLayer = (object: Object3D, root: Object3D) => {
  for (let x: Object3D | null = object; x && x !== root; x = x.parent) {
    if (isLayer(x)) {
      return true;
    }
  }

  return false;
};

/**
 * Whether something is drawn in the object outside the layers below it. The items of a drawing
 * that is drawn together are in the layers of their primitives, see StyledLines.layerObjects.
 */
const hasContentOutsideLayers = (object: Object3D): boolean =>
  object.children.some((child) => {
    if (isLayer(child)) {
      return false;
    }

    if (child instanceof StyledLines && child.layerObjects.length > 0) {
      return child.layerObjects.some((layer) => !isInLayer(layer, object));
    }

    return (
      child instanceof Mesh ||
      child instanceof Line ||
      child instanceof Points ||
      hasContentOutsideLayers(child)
    );
  });

/**
 * Gets the name of a node from its extras where it has one, since the loader replaces spaces and
 * some other characters in the names of nodes. An IFC node without a name gets none: it is named by
 * what it is, see LayerTreeNode.ifcClass.
 */
function getName(object: Object3D): string {
  const ifc = getIfcData(object);

  if (ifc) {
    return typeof ifc.attributes?.Name === 'string' ? ifc.attributes.Name.trim() : '';
  }

  return getDrawingLayer(object)?.layer ?? object.name;
}

/**
 * Gets the layers below the object, in a tree: the layers of the pages of a PDF or of a DWG, or
 * the objects of an IFC model (project, site, building, storeys and the elements in them), with
 * the other nodes from a converter that have layers below them, such as the pages of a PDF. The
 * model space of a DWG is listed only when something is drawn in it outside its layers, which it
 * otherwise only holds.
 */
export function getLayerTree(object: Object3D): LayerTreeNode[] {
  return object.children.flatMap((child) => {
    const children = getLayerTree(child);
    const page = (child.userData.pdf as { page?: unknown } | undefined)?.page;
    const isDwgModel = isDefined(child.userData.dwg) && !isLayer(child);

    return isLayer(child) ||
      (children.length > 0 &&
        isConverterNode(child) &&
        (!isDwgModel || hasContentOutsideLayers(child)))
      ? [
          {
            object: child,
            name: getName(child),
            page: typeof page === 'number' ? page : undefined,
            ifcClass: getIfcData(child)?.class,
            children,
          },
        ]
      : children;
  });
}

/**
 * The most nodes that getNodeTree lists, so that the dialog stays usable for large models.
 */
export const maxNodeTreeNodes = 500;

/**
 * Gets the nodes of a glb file in a tree, for files without layers from a converter (see
 * getLayerTree), with their names from the file. The tree is filled breadth first with at most
 * maxNodes, so that the top of a large model is listed, and the nodes below the last listed ones
 * are hidden with them. Nodes that only wrap the rest (the only node at the top) are left out.
 *
 * @param names The glTF nodes, with their names in the file, which the loader changes.
 */
export function getNodeTree(
  root: Object3D,
  names: Map<Object3D, string>,
  maxNodes = maxNodeTreeNodes,
): LayerTreeNode[] {
  const getNodes = (object: Object3D) => object.children.filter((x) => names.has(x));
  let top = getNodes(root);

  while (top.length === 1 && getNodes(top[0]).length > 0) {
    top = getNodes(top[0]);
  }

  const tree: LayerTreeNode[] = [];
  let level = [{ nodes: top, siblings: tree }];
  let count = 0;

  while (level.length > 0 && count < maxNodes) {
    const next: typeof level = [];

    for (const { nodes, siblings } of level) {
      for (const node of nodes.slice(0, maxNodes - count)) {
        const entry: LayerTreeNode = { object: node, name: names.get(node) ?? '', children: [] };
        siblings.push(entry);
        count++;

        const children = getNodes(node);
        if (children.length > 0) {
          next.push({ nodes: children, siblings: entry.children });
        }
      }
    }

    level = next;
  }

  return tree;
}

/**
 * Visits the layers of the tree, with a path that identifies each layer by the names of the layers
 * down to it, also in another version of the file. A name that is repeated among siblings gets the
 * number of the repetition, since names are not unique in IFC.
 */
function forEachLayerPath(
  tree: LayerTreeNode[],
  visit: (node: LayerTreeNode, path: string) => void,
  parentPath = '',
) {
  const counts = new Map<string, number>();

  for (const node of tree) {
    const name = node.page !== undefined ? `page ${node.page}` : node.name;
    const count = counts.get(name) ?? 0;
    counts.set(name, count + 1);

    const path = `${parentPath}/${name}${count > 0 ? `#${count}` : ''}`;
    visit(node, path);
    forEachLayerPath(node.children, visit, path);
  }
}

/**
 * Gets whether each layer of the tree is shown, by its path.
 */
export function getLayerVisibility(tree: LayerTreeNode[]): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  forEachLayerPath(tree, (node, path) => (visibility[path] = node.object.visible));
  return visibility;
}

/**
 * Shows and hides the layers of the tree as in the visibility from getLayerVisibility. Layers that
 * are not in it are left as they are.
 */
export function setLayerVisibility(tree: LayerTreeNode[], visibility: Record<string, boolean>) {
  forEachLayerPath(tree, (node, path) => {
    const visible = visibility[path];

    if (typeof visible === 'boolean') {
      node.object.visible = visible;
    }
  });
}

/**
 * Shows only the layer, with the layers below it and the layers above it that it is shown with,
 * and hides all other layers.
 */
export function showOnlyLayer(tree: LayerTreeNode[], layer: LayerTreeNode) {
  // Returns whether the layer is among the nodes or below them.
  const show = (nodes: LayerTreeNode[], belowLayer: boolean): boolean => {
    let found = false;

    for (const node of nodes) {
      const isLayer = node === layer;
      const isAboveLayer = show(node.children, belowLayer || isLayer);
      node.object.visible = belowLayer || isLayer || isAboveLayer;
      found ||= isLayer || isAboveLayer;
    }

    return found;
  };

  show(tree, false);
}
